<template>
  <div class="summary-tab">
    <!-- Header Section -->
    <div class="summary-header mb-6">
      <h2 class="text-h5 font-weight-bold mb-2">Rating Summary</h2>
      <p class="text-body-1 text-medium-emphasis">
        Aggregated summary of all ratings and review points
      </p>
    </div>

    <!-- Average Ratings Section -->
    <v-row class="mb-8">
      <v-col cols="12" md="6">
        <v-card class="rating-card pa-6" elevation="2">
          <div class="d-flex align-center mb-4">
            <div class="rating-icon-wrapper mr-4">
              <v-icon color="white" size="32">mdi-account-star</v-icon>
            </div>
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">
                Average Client Rating to squad
              </div>
              <div class="text-h3 font-weight-bold text-primary mt-1">
                {{ avgClientToTalent.toFixed(1) }}
              </div>
            </div>
          </div>
          <HalfStarRating
            :value="avgClientToTalent"
            class="mb-4"
            color="primary"
            :max="5"
          />
          <div class="rating-stats mt-4">
            <div class="stat-item mb-3">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption text-medium-emphasis"
                  >Total Reviews</span
                >
                <span class="text-body-2 font-weight-medium">{{
                  countClientToTalent
                }}</span>
              </div>
              <v-progress-linear
                :model-value="100"
                color="grey-lighten-3"
                height="4"
                rounded
              ></v-progress-linear>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card
          class="rating-card pa-6"
          elevation="2"
          style="position: relative"
        >
          <div class="coming-soon-overlay">
            <div class="coming-soon-content">
              <v-icon color="white" size="48" class="mb-3"
                >mdi-clock-outline</v-icon
              >
              <div class="text-h6 font-weight-bold text-white mb-1">
                Coming Soon
              </div>
              <div class="text-body-2 text-white text-medium-emphasis">
                This feature is under development
              </div>
            </div>
          </div>
          <div class="d-flex align-center mb-4">
            <div class="rating-icon-wrapper mr-4">
              <v-icon color="white" size="32">mdi-star-circle</v-icon>
            </div>
            <div>
              <div class="text-subtitle-1 text-medium-emphasis">
                Average Talent Rating to Client
              </div>
              <div class="text-h3 font-weight-bold text-amber mt-1">
                {{ 0 }}
              </div>
            </div>
          </div>
          <v-rating
            :value="0"
            readonly
            dense
            color="amber"
            size="28"
            class="mb-4"
          ></v-rating>
          <div class="rating-stats mt-4">
            <div class="stat-item mb-3">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-caption text-medium-emphasis"
                  >Total Reviews</span
                >
                <span class="text-body-2 font-weight-medium">{{ 0 }}</span>
              </div>
              <v-progress-linear
                :model-value="100"
                color="grey-lighten-3"
                height="4"
                rounded
              ></v-progress-linear>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Review Points Summary -->
    <div class="summary-section mb-6">
      <h3 class="text-h6 font-weight-bold mb-4">Review Points Summary</h3>
      <v-row>
        <v-col cols="12" md="6">
          <v-card class="insight-card mb-4" elevation="2">
            <v-card-title class="d-flex align-center pa-6 pb-4">
              <div class="d-flex align-center">
                <div class="icon-wrapper success mr-4">
                  <v-icon color="white" size="24">mdi-thumb-up</v-icon>
                </div>
                <div>
                  <div class="text-h6 font-weight-bold">Key Strengths</div>
                  <div class="text-caption text-medium-emphasis">
                    Most frequently mentioned positive attributes
                  </div>
                </div>
              </div>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <div class="chip-list">
                <template v-if="keyStrengths.length > 0">
                  <v-chip
                    v-for="(strength, index) in keyStrengths"
                    :key="'strength-' + index"
                    color="success"
                    variant="tonal"
                    class="ma-1 font-weight-medium strength-chip"
                    size="large"
                    pill
                  >
                    <v-icon left size="small" color="success"
                      >mdi-check-circle</v-icon
                    >
                    {{ strength }}
                  </v-chip>
                </template>
                <template v-else>
                  <div class="text-center text-medium-emphasis py-4">
                    No strengths available.
                  </div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="insight-card mb-4" elevation="2">
            <v-card-title class="d-flex align-center pa-6 pb-4">
              <div class="d-flex align-center">
                <div class="icon-wrapper error mr-4">
                  <v-icon color="white" size="24">mdi-alert-circle</v-icon>
                </div>
                <div>
                  <div class="text-h6 font-weight-bold">
                    Areas for Improvement
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    Most frequently mentioned areas to enhance
                  </div>
                </div>
              </div>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <div class="chip-list">
                <template v-if="areasForImprovement.length > 0">
                  <v-chip
                    v-for="(area, index) in areasForImprovement"
                    :key="'area-' + index"
                    color="error"
                    variant="tonal"
                    class="ma-1 font-weight-medium improvement-chip"
                    size="large"
                    pill
                  >
                    <v-icon left size="small" color="error">mdi-alert</v-icon>
                    {{ area }}
                  </v-chip>
                </template>
                <template v-else>
                  <div class="text-center text-medium-emphasis py-4">
                    No areas for improvement available.
                  </div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import HalfStarRating from "@/components/shared/HalfStarRating.vue";
export default {
  name: "SummaryTab",
  components: {
    HalfStarRating,
  },
  props: {
    selectedProject: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      // These will be set from the API response
      talentToClientRatings: [4.5, 5, 4, 4.8, 4.2, 4.7],
      clientToTalentRatings: [], // will be set from API
      reviewPoints: [], // will be set from API
      avgClientToTalent: 0,
      countClientToTalent: 0,
      keyStrengthsData: [], // renamed to avoid conflict
      areasForImprovementList: [],
    };
  },
  watch: {
    selectedProject: {
      handler(newProject, oldProject) {
        // Only refetch if project actually changed and we have a valid project
        if (newProject && newProject !== oldProject) {
          this.fetchData();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions({
      getClientRatings: "clients/getClientRatings",
    }),
    fetchData() {
      this.getClientRatings({
        project_id: this.selectedProject || this.$route.params.project_id,
        rater_id: this.$route.params.id,
        rater_type: "client",
        summary: true,
      }).then((res) => {
        // Set values from API response
        this.avgClientToTalent = res.avg_client_to_squad || 0;
        this.countClientToTalent = res.count_client_to_squad || 0;
        this.clientToTalentRatings = Array(this.countClientToTalent).fill(
          this.avgClientToTalent
        );
        this.keyStrengthsData = (res.options?.positive || []).map(
          (opt) => opt.label
        );
        // Optionally, handle negative options for areasForImprovement
        this.areasForImprovementList = (res.options?.negative || []).map(
          (opt) => opt.label
        );
      });
    },
  },
  computed: {
    averageTalentRating() {
      if (!this.talentToClientRatings.length) return 0;
      return (
        this.talentToClientRatings.reduce((a, b) => a + b, 0) /
        this.talentToClientRatings.length
      );
    },
    averageClientRating() {
      // Use the value from API if available
      if (this.avgClientToTalent) return this.avgClientToTalent;
      if (!this.clientToTalentRatings.length) return 0;
      return (
        this.clientToTalentRatings.reduce((a, b) => a + b, 0) /
        this.clientToTalentRatings.length
      );
    },
    keyStrengths() {
      // Use the mapped strengths from API
      return this.keyStrengthsData && this.keyStrengthsData.length
        ? this.keyStrengthsData
        : [];
    },
    areasForImprovement() {
      // Use the mapped improvements from API
      return this.areasForImprovementList && this.areasForImprovementList.length
        ? this.areasForImprovementList
        : [];
    },
    positiveReviewsCount() {
      const positiveCount = this.talentToClientRatings.filter(
        (rating) => rating >= 4
      ).length;
      return Math.round(
        (positiveCount / this.talentToClientRatings.length) * 100
      );
    },
    positiveClientReviewsCount() {
      const positiveCount = this.clientToTalentRatings.filter(
        (rating) => rating >= 4
      ).length;
      return Math.round(
        (positiveCount / this.clientToTalentRatings.length) * 100
      );
    },
  },
};
</script>

<style scoped>
.summary-tab {
  padding: 1.5rem;
}

.summary-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 1rem;
}

.rating-card {
  border-radius: 16px;
  background: linear-gradient(to bottom right, #ffffff, #f8fafc);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.rating-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.rating-icon-wrapper .v-icon {
  color: white !important;
}

.rating-stats {
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.stat-item {
  margin-bottom: 1rem;
}

.insight-card {
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 220px;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.icon-wrapper.success {
  background: linear-gradient(135deg, #34d399 0%, #059669 100%);
}

.icon-wrapper.error {
  background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
}

.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.9),
    rgba(248, 250, 252, 0.9)
  );
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.strength-chip {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%) !important;
  color: #166534 !important;
  border: 1px solid #86efac !important;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.1);
}

.improvement-chip {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%) !important;
  color: #991b1b !important;
  border: 1px solid #fca5a5 !important;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.1);
}

.v-chip {
  border: 1px solid transparent;
  font-weight: 500;
}

.v-chip .v-icon {
  margin-right: 4px;
}

.text-amber {
  color: #f59e0b !important;
}

.text-primary {
  color: #3b82f6 !important;
}

.text-success {
  color: #10b981 !important;
}

/* Custom progress bar colors */
.v-progress-linear--success {
  background-color: #dcfce7 !important;
}

.v-progress-linear--success .v-progress-linear__determinate {
  background-color: #10b981 !important;
}

.v-progress-linear--grey-lighten-3 {
  background-color: #f1f5f9 !important;
}

.v-progress-linear--grey-lighten-3 .v-progress-linear__determinate {
  background-color: #e2e8f0 !important;
}

.coming-soon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: not-allowed;
}

.coming-soon-content {
  text-align: center;
  padding: 2rem;
}
</style>
